import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Resource {
  category: string;
  categoryIcon: string;
  title: string;
  description: string;
  type: 'Article' | 'Guide' | 'Document' | 'Checklist';
  readTime: string;
  date: string;
  featured?: boolean;
}

const resources: Resource[] = [
  {
    category: 'Roofing Basics',
    categoryIcon: 'ri-home-4-line',
    title: 'How to Read a Roofing Quote: What Contractors Don\'t Tell You',
    description: 'Most homeowners sign roofing contracts without fully understanding what\'s included — or excluded. This guide breaks down every line item, what to watch for, and the red flags that cost homeowners thousands.',
    type: 'Guide',
    readTime: '8 min read',
    date: 'March 2025',
    featured: true,
  },
  {
    category: 'Bid Comparison',
    categoryIcon: 'ri-bar-chart-grouped-line',
    title: 'Roofing Bid Comparison Checklist',
    description: 'A printable, step-by-step checklist for evaluating and comparing roofing bids side-by-side. Covers materials, scope, warranties, labor, and contractor credentials.',
    type: 'Checklist',
    readTime: '3 min read',
    date: 'February 2025',
    featured: true,
  },
  {
    category: 'Wake County Market',
    categoryIcon: 'ri-map-pin-line',
    title: 'What Roofs Really Cost in Wake County, NC (2024–2025 Data)',
    description: 'Actual price ranges from hundreds of real roof replacements in Raleigh, Cary, Apex, and surrounding areas. See what your neighbors paid — and what\'s actually fair for your square footage.',
    type: 'Article',
    readTime: '6 min read',
    date: 'January 2025',
    featured: true,
  },
  {
    category: 'Contractor Vetting',
    categoryIcon: 'ri-shield-check-line',
    title: '7 Questions to Ask Every Roofing Contractor Before Signing',
    description: 'Before you hand over a deposit, make sure you\'ve asked these critical questions. We\'ve seen what goes wrong when homeowners skip this step — and it\'s expensive.',
    type: 'Article',
    readTime: '5 min read',
    date: 'December 2024',
  },
  {
    category: 'Insurance Claims',
    categoryIcon: 'ri-file-shield-2-line',
    title: 'Navigating Roof Insurance Claims in North Carolina',
    description: 'A practical guide for homeowners filing storm or hail damage claims. What adjusters look for, how to protect yourself, and why having an independent advocate matters.',
    type: 'Guide',
    readTime: '10 min read',
    date: 'November 2024',
  },
  {
    category: 'Materials',
    categoryIcon: 'ri-stack-line',
    title: 'Asphalt Shingles vs. Architectural Shingles: Which Is Worth It?',
    description: 'A side-by-side breakdown of the most common roofing materials used in Wake County — longevity, cost difference, warranty implications, and what contractors prefer to install (and why).',
    type: 'Article',
    readTime: '7 min read',
    date: 'October 2024',
  },
  {
    category: 'Process',
    categoryIcon: 'ri-route-line',
    title: 'The Complete Roof Replacement Timeline: What to Expect',
    description: 'From first inspection to final walkthrough — a realistic week-by-week breakdown of what happens during a roof replacement and how to stay in control of the process.',
    type: 'Guide',
    readTime: '6 min read',
    date: 'September 2024',
  },
  {
    category: 'Red Flags',
    categoryIcon: 'ri-alert-line',
    title: '10 Roofing Contractor Red Flags (And What They Actually Mean)',
    description: 'Storm chasers, unlicensed subs, missing permit pulls — learn to spot the warning signs before they become expensive problems. Based on real contractor patterns seen in Wake County.',
    type: 'Article',
    readTime: '5 min read',
    date: 'August 2024',
  },
  {
    category: 'Roofing Basics',
    categoryIcon: 'ri-home-4-line',
    title: 'Ventilation, Underlayment, and Flashing: The Hidden Layers of a Quality Roof',
    description: 'The parts of a roof you never see are often the most important. Here\'s what separates a roof that lasts 30 years from one that fails in 10 — and how to verify what your contractor is actually installing.',
    type: 'Article',
    readTime: '8 min read',
    date: 'July 2024',
  },
  {
    category: 'Document',
    categoryIcon: 'ri-file-text-line',
    title: 'NC Roofing Contractor License Verification Guide',
    description: 'Step-by-step instructions for verifying a roofing contractor\'s NC license, insurance certificate, and Better Business Bureau standing — before you sign anything.',
    type: 'Document',
    readTime: '2 min read',
    date: 'June 2024',
  },
];

const typeColors: Record<string, string> = {
  Article: 'bg-slate-100 text-slate-700',
  Guide: 'bg-emerald-100 text-emerald-700',
  Checklist: 'bg-amber-100 text-amber-700',
  Document: 'bg-rose-100 text-rose-700',
};

const categories = ['All', ...Array.from(new Set(resources.map(r => r.category)))];

export default function ResourcesPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://overunderroofing.com';

    // --- CollectionPage Schema ---
    const existing = document.getElementById('resources-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'resources-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Roofing Resources for Wake County Homeowners',
      description:
        'Free roofing guides, bid comparison checklists, and Wake County market data for homeowners in Raleigh, Cary, Apex & Triangle NC. Make smarter roofing decisions with expert resources.',
      url: `${siteUrl}/resources`,
      isPartOf: { '@type': 'WebSite', name: 'OverUnder Roofing', url: siteUrl },
      publisher: {
        '@type': 'Organization',
        name: 'OverUnder Roofing',
        url: siteUrl,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Resources', item: `${siteUrl}/resources` },
        ],
      },
      hasPart: resources.map((r, i) => ({
        '@type': 'Article',
        position: i + 1,
        name: r.title,
        description: r.description,
        datePublished: r.date,
        articleSection: r.category,
      })),
    });
    document.head.appendChild(script);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      const el = document.getElementById('resources-schema');
      if (el) el.remove();
    };
  }, []);

  const featured = resources.filter(r => r.featured);
  const filtered = activeCategory === 'All'
    ? resources.filter(r => !r.featured)
    : resources.filter(r => r.category === activeCategory && !r.featured);

  return (
    <div className="min-h-screen bg-white">
      {/* React 19 document metadata */}
      <title>Roofing Resources for Wake County Homeowners – Guides & Checklists | OverUnder Roofing</title>
      <meta name="description" content="Free roofing guides, bid comparison checklists, and Wake County market data for homeowners in Raleigh, Cary, Apex & Triangle NC. Make smarter roofing decisions with expert resources." />
      <link rel="canonical" href="https://overunderroofing.com/resources" />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/95 shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/82f8a00e-bc28-4af6-bf3f-740e76e3ab6b.png"
              alt="OverUnder Roofing Logo – Independent Roofing Advocate Wake County NC"
              className="h-10 w-auto"
            />
            <div>
              <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                OverUnder Roofing
              </p>
              <p className="text-xs italic text-gray-500" style={{ fontFamily: 'Playfair Display, serif' }}>
                The House Always Wins
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-600 hover:text-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
              &larr; Back to Home
            </Link>
            <Link
              to="/wake-county-simulation"
              className="text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Market Data
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 px-4 py-2 rounded-full mb-6">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-book-open-line text-emerald-700 text-sm"></i>
            </div>
            <span className="text-sm font-semibold text-emerald-700">Homeowner Education Center</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Roofing Resources for<br />Wake County Homeowners
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Articles, guides, checklists, and documents to help you make smarter roofing decisions — without relying on a contractor to explain it to you.
          </p>
        </div>
      </section>

      {/* Featured Resources */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-sm font-bold uppercase tracking-widest text-emerald-600 mb-8">Featured Resources</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {featured.map((resource, i) => (
              <article
                key={i}
                className="group bg-slate-50 rounded-2xl border border-slate-100 p-7 flex flex-col gap-4 hover:border-emerald-200 hover:bg-emerald-50/30 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-100">
                      <i className={`${resource.categoryIcon} text-emerald-600 text-sm`}></i>
                    </div>
                    <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">{resource.category}</span>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${typeColors[resource.type]}`}>
                    {resource.type}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 leading-snug mb-3 group-hover:text-emerald-800 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {resource.description}
                  </p>
                </div>
                <div className="flex items-center gap-3 mt-auto pt-2 border-t border-slate-100">
                  <span className="text-xs text-gray-400">{resource.date}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs text-gray-400">{resource.readTime}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* All Resources with filter */}
      <section className="py-16 bg-slate-50/60 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 mb-10">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-600">All Resources</h2>
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer border ${
                    activeCategory === cat
                      ? 'bg-emerald-700 text-white border-emerald-700'
                      : 'bg-white text-gray-600 border-slate-200 hover:border-emerald-300 hover:text-emerald-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {(activeCategory === 'All' ? resources.filter(r => !r.featured) : filtered).map((resource, i) => (
              <article
                key={i}
                className="group bg-white rounded-xl border border-slate-100 p-6 flex gap-5 items-start hover:border-emerald-200 transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100 shrink-0 group-hover:bg-emerald-50 transition-colors">
                  <i className={`${resource.categoryIcon} text-xl text-emerald-600`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${typeColors[resource.type]}`}>
                      {resource.type}
                    </span>
                    <span className="text-xs text-gray-400">{resource.category}</span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 leading-snug mb-2 group-hover:text-emerald-800 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
                    {resource.description}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs text-gray-400">{resource.date}</span>
                    <span className="text-gray-300 text-xs">·</span>
                    <span className="text-xs text-gray-400">{resource.readTime}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {(activeCategory !== 'All' && filtered.length === 0) && (
            <div className="text-center py-16">
              <p className="text-gray-400 text-sm">No resources found in this category yet. Check back soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-800">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-emerald-700 mx-auto mb-6">
            <i className="ri-shield-user-line text-3xl text-emerald-200"></i>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Stop Guessing?
          </h2>
          <p className="text-emerald-200 text-lg mb-8 leading-relaxed">
            Reading guides is a great start. Having an expert in your corner is better. We handle the hard part — so you don't have to.
          </p>
          <Link
            to="/#contact"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-2xl font-semibold text-base transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Get Started – $500 Flat Fee
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-400">© 2025 OverUnder Roofing. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-emerald-400 transition-colors cursor-pointer">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms" className="hover:text-emerald-400 transition-colors cursor-pointer">Terms of Service</Link>
            <span>|</span>
            <Link to="/accessibility" className="hover:text-emerald-400 transition-colors cursor-pointer">Accessibility</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
